import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { FormControl, UntypedFormControl } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import Cropper from 'cropperjs';
import { Subscription } from 'rxjs';
import { ImageDimensions } from '../../image-uploader.service';
import { FileInfo } from '../../uploader.interface';

const ZOOM_STEP_SIZE = 0.05;

export type AspectRatioPreset = {
  label: string;
  value: number;
  selected?: boolean;
};

@Component({
  selector: 'glxy-image-editor',
  templateUrl: './image-editor.component.html',
  styleUrls: ['./image-editor.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class GalaxyImageEditorComponent implements AfterViewInit, OnDestroy {
  @HostBinding('class') class = 'glxy-image-editor';

  /** Contains the image that we will edit */
  @Input() set fileInfo(fileInfo: FileInfo) {
    /** Creates a safe url for rendering in an image tag */
    if (fileInfo.file) {
      this.safeUrl = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(fileInfo.file));
    }
    this.fileType = fileInfo?.file?.type;
  }

  /** Optional: maximum dimensions allowed for the edited output image */
  @Input() maxDimensions?: ImageDimensions;

  /** Optional: minimum dimensions allowed for the edited output image */
  @Input() minDimensions?: ImageDimensions;

  /** Optional: list of aspect ratios allowed to be selected. If only one, it will be selected and no dropdown shown */
  @Input() aspectRatios: AspectRatioPreset[] = [
    {
      label: 'GALAXY.UPLOADER.FREE_ASPECT',
      value: 0,
    },
  ];

  /** Maximum height of the editor window */
  @Input() maxEditorHeight?: string = 'auto';

  /** Alignment of the editor save/cancel actions */
  @Input() align?: 'start' | 'end';

  /** The automatic cropping applied. Should be a number between 0 and 1 (for percentage) **/
  @Input() autoCropArea: number | undefined = undefined;

  /** Minimum container height for the cropper container **/
  @Input() minContainerHeight: number | undefined = undefined;

  /** Emits the resulting edited image */
  @Output() closed: EventEmitter<Blob | null> = new EventEmitter();

  @ViewChild('sourceImage', { read: ElementRef }) imageEl!: ElementRef;

  safeUrl?: SafeUrl;
  aspectControl = new FormControl<number | null>(null);
  zoomStep = ZOOM_STEP_SIZE;
  isDisabled = false;
  minZoom = 0.01;
  maxZoom = 2.0;
  zoomControl = new UntypedFormControl((this.maxZoom - this.minZoom) / 2);
  fileType?: string;

  cropper?: Cropper;
  private subscriptions: Subscription[] = [];
  private sanitizer = inject(DomSanitizer);

  ngAfterViewInit(): void {
    const imageEl = this.imageEl.nativeElement;

    if (imageEl && !this.cropper) {
      this.cropper = new Cropper(this.imageEl?.nativeElement, {
        minContainerHeight: this.minContainerHeight,
        autoCropArea: this.autoCropArea,
      });
    }

    let initialAspect;

    if (this.aspectRatios.length > 1) {
      // If there are multiple aspect ratios:
      // Subscribe to aspect ratio changes and update when the user selects a new aspect ratio
      this.subscriptions.push(
        this.aspectControl.valueChanges.subscribe((aspect) => {
          if (aspect !== null) this.onAspectChange(aspect);
        }),
      );
      // Check if there is a preselected aspect ratio
      const preselectedValue = this.aspectRatios.find((option) => option.selected === true);
      // If there is no preselected value, set the initial aspect ratio to the first aspect ratio
      initialAspect = preselectedValue?.value ?? this.aspectRatios[0].value;
      this.aspectControl.setValue(initialAspect);
    } else {
      // If there is only one aspect ratio, set the initial aspect ratio to that one
      // If none are provided, set the initial aspect ratio to 0 (free aspect)
      initialAspect = this.aspectRatios[0]?.value ?? 0;
      this.aspectControl.setValue(initialAspect);
      this.onAspectChange(initialAspect);
    }

    this.subscriptions.push(
      this.zoomControl.valueChanges.subscribe({
        next: (value: number) => this.cropper?.zoomTo(value),
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());

    if (this.cropper?.destroy) {
      this.cropper.destroy();
    }
  }

  /**
   * Close and consume the edited image.
   *
   * @param [save] - If true, will output the edited image
   */
  onClose(save?: boolean): void {
    this.isDisabled = true;

    if (!save) {
      return this.closed.emit();
    }

    this.cropper
      ?.getCroppedCanvas({
        maxWidth: this.maxDimensions?.width,
        maxHeight: this.maxDimensions?.height,
        minWidth: this.minDimensions?.width,
        minHeight: this.minDimensions?.height,
      })
      .toBlob((blob: Blob | null) => {
        this.closed.emit(blob);
      }, this.fileType || 'image/jpeg');
  }

  /**
   * React to aspect ratio constraint changes
   * @param aspect - The aspect ratio to set the cropper instance to use
   */
  onAspectChange(aspect: number): void {
    this.cropper?.setAspectRatio(aspect);
  }

  /** Zooms in the cropper instance a single step */
  zoomIn(): void {
    this.cropper?.zoom(this.zoomStep);
  }

  /** Zooms out the cropper instance a single step */
  zoomOut(): void {
    this.cropper?.zoom(-this.zoomStep);
  }
}
